<script lang="ts">
import { reactive, defineComponent, computed } from '@vue/composition-api';
import { useCurrentUserModule, DeleteJiraAuthToken } from '@/store/global/currentUser';
import { getInitials } from '@/_core/utils/misc';
import { trackingService } from '@/_core/tracking/trackingService';
import { TrackLogout } from '@/_core/tracking/userActionsToTrack/user/trackLogout';

import intercomService from '@/_core/utils/intercom';

import { camelCaseToSentence } from '@/_core/utils/misc';
import { confirm, snackbar } from '@/_core/app-components/use';
import { currentUserWsRoles } from '@/_core/utils/roles';

import ColoredAvatar from '@/pages/components/coloredAvatar/ColoredAvatar.vue';

const UserNavGroup = defineComponent({
    name: 'UserNavGroup',
    components: {
        ColoredAvatar,
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setup(props, ctx) {
        const currentUserStore = useCurrentUserModule();

        const state = reactive({
            userName: computed(() => currentUserStore.state.fullName),
            userEmail: computed(() => currentUserStore.state.loginName),
            hasJiraAuthToken: computed(() => currentUserStore.state.hasJiraAuthToken),

            logoutUrl: initAppData.appUrls.logoutUrl.toLowerCase(),

            isWsContext: initAppData.isInWsContext,
            originalUserRole: initAppData.currentWs ? initAppData.currentWs.originalUserRole : '',
            workspaceCount: initAppData.currentUser.workspaceCount,
            workspaces: initAppData.currentUser.workspaces,

            hasRightsToManageUsersOrWs:
                initAppData.isInWsContext && (currentUserWsRoles.isWorkspaceAdmin || currentUserWsRoles.isSubscriptionAdmin),
            hasWorkspaceSettingsAccess:
                initAppData.isInWsContext && (currentUserWsRoles.isWorkspaceAdmin || currentUserWsRoles.isSubscriptionAdmin),

            helpLinkWorkspaceRoles: helpLinks.helpLinkWorkspaceRoles,
            helpLinkHelpCenter: helpLinks.helpLinkHelpCenter,
            rootUrl: initAppData.appUrls.rootUrl,
            isMenuOpen: false,
            isSubscriptionAdmin: currentUserWsRoles.isSubscriptionAdmin,
            isWorkspaceUnderCorporateSubscription: initAppData.currentWs?.isUnderCorporateSubscription ?? false,
        });

        const currentWsName = computed(() => (state.isWsContext ? initAppData.currentWs.name : ''));

        const openUserProfile = () => {
            ctx.emit('openUserProfile');
        };

        const openNotificationSettings = () => {
            ctx.emit('openNotificationSettings');
        };

        const openApiTokenDialog = () => {
            state.isMenuOpen = false;
            ctx.emit('openApiTokenDialog');
        };

        const openIntercom = () => {
            intercomService.showMessage();
        };

        const onlogOut = () => {
            window.location.href = state.logoutUrl;
            trackingService.TrackUserAction(new TrackLogout());
        };

        const onDeleteJiraUserToken = () => {
            confirm.show({
                title: 'Are you sure you want to delete your Jira user token?',
                message:
                    'Please confirm that you want to delete your Jira user token. <span class="font-weight-bold">By deleting this you will have the option to authenticate again with the correct Jira user</span>',
                acceptBtnText: 'Delete my Jira user token',
                cancelBtnText: 'Cancel',
                acceptFn: async () => {
                    const res = await currentUserStore.dispatch(new DeleteJiraAuthToken());

                    if (res) {
                        snackbar.showSuccess('Jira token deleted successfully');
                    }
                },
                width: 600,
            });
        };

        return {
            state,
            currentWsName,
            openUserProfile,
            openNotificationSettings,
            openApiTokenDialog,
            openIntercom,
            getInitials,
            onlogOut,
            camelCaseToSentence,
            onDeleteJiraUserToken,
        };
    },
});

export default UserNavGroup;
</script>

<template>
  <v-menu v-model="state.isMenuOpen" offset-x z-index="16">
    <template #activator="{ on, attrs }">
      <v-list-item v-bind="attrs" v-on="on">
        <ColoredAvatar class="mr-4" :name="state.userName" />

        <v-list-item-content style="min-width: 0;" class="ellipsis">
          <v-list-item-title>{{ state.userName }}</v-list-item-title>
          <v-list-item-subtitle>{{ camelCaseToSentence(state.originalUserRole) }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>

    <v-card min-width="290" max-width="320">
      <v-list>
        <v-list-item>
          <ColoredAvatar class="mr-4" :name="state.userName" />

          <v-list-item-content>
            <v-list-item-title>{{ state.userName }}</v-list-item-title>
            <v-list-item-subtitle>{{ state.userEmail }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="state.isWsContext" class="mt-n3">
          <v-list-item-content>
            <v-list-item-subtitle>
              You're a(n) <span class="font-weight-bold">{{ camelCaseToSentence(state.originalUserRole) }}</span> on this
            </v-list-item-subtitle>
            <v-list-item-subtitle>
              workspace.
              <a :href="state.helpLinkWorkspaceRoles" target="_blank" class="font-weight-bold">Learn about user roles.</a>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <div v-if="state.isWsContext" class="px-4 font-weight-semibold" style="font-size: 16px;">
        {{ currentWsName }}
      </div>

      <v-list nav class="pt-0" dense>
        <v-menu offset-x>
          <template #activator="{ on: viewAdvancedSettingsOn, attrs: viewAdvancedSettingsAttrs }">
            <v-list-item v-bind="viewAdvancedSettingsAttrs" @click.stop.prevent v-on="viewAdvancedSettingsOn">
              <v-list-item-icon>
                <v-icon>$grid-light</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Switch or create workspaces</v-list-item-title>
              <v-list-item-icon>
                <v-icon small>
                  $next
                </v-icon>
              </v-list-item-icon>
            </v-list-item>
          </template>
          <v-card>
            <v-list nav dense>
              <div style="max-height: 200px; overflow-y: auto;" class="thin-scrollbar">
                <v-list-item
                  v-for="workspace in state.workspaces"
                  :key="workspace.url"
                  :href="workspace.url"
                  @click="state.isMenuOpen = false"
                >
                  <v-list-item-icon>
                    <v-icon>$grid-light</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ workspace.name }}</v-list-item-title>
                </v-list-item>
              </div>

              <v-list-item to="/newworkspace" @click="state.isMenuOpen = false">
                <v-list-item-icon>
                  <v-icon>
                    $add-item
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  Create a workspace
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <v-list-item v-if="state.hasWorkspaceSettingsAccess" :to="{ name: 'workspacesettings' }">
          <v-list-item-icon>
            <v-icon>
              $falSlidersVSquare
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Workspace settings
          </v-list-item-title>
        </v-list-item>
            
        <template v-if="state.isSubscriptionAdmin && !state.isWorkspaceUnderCorporateSubscription">
          <v-list-item :to="{ name: 'managesubscription' }">
            <v-list-item-icon>
              <v-icon>
                $falFileInvoice
              </v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              Manage Subscription
            </v-list-item-title>
          </v-list-item>
        </template>

        <v-list-item 
          v-if="state.isSubscriptionAdmin && !state.isWorkspaceUnderCorporateSubscription"
          to="/billing"
        >
          <v-list-item-icon>
            <v-icon>$Bill-light</v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Billing
          </v-list-item-title>
        </v-list-item>

        <v-divider />

        <v-list-item @click="openUserProfile">
          <v-list-item-icon>
            <v-icon>
              $user-circle-light
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Personal settings
          </v-list-item-title>
        </v-list-item>

        <v-list-item @click="openNotificationSettings">
          <v-list-item-icon>
            <v-icon>
              $bell-light
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Notification settings
          </v-list-item-title>
        </v-list-item>

        <template v-if="state.hasRightsToManageUsersOrWs">
          <v-divider />
          <v-list-item to="/integrationsettings">
            <v-list-item-icon>
              <v-icon>
                $fabJira
              </v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              Jira integration settings
            </v-list-item-title>
          </v-list-item>
        </template>

        <v-list-item to="/ai">
          <v-list-item-icon>
            <v-icon>
              $fasSparkles
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            AI Assist
          </v-list-item-title>
        </v-list-item>

        <v-divider />

        <v-menu offset-x>
          <template #activator="{ on: viewAdvancedSettingsOn, attrs: viewAdvancedSettingsAttrs }">
            <v-list-item v-bind="viewAdvancedSettingsAttrs" @click.stop.prevent v-on="viewAdvancedSettingsOn">
              <v-list-item-icon>
                <v-icon>$slider-circle-light</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                Advanced settings
              </v-list-item-title>
              <v-list-item-icon>
                <v-icon small>
                  $next
                </v-icon>
              </v-list-item-icon>
            </v-list-item>
          </template>
          <v-card>
            <v-list nav dense>
              <v-list-item to="/domaincontrol" @click="state.isMenuOpen = false">
                <v-list-item-icon>
                  <v-icon>$web-light</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Domain control</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openApiTokenDialog">
                <v-list-item-icon>
                  <v-icon>$repository-light</v-icon>
                </v-list-item-icon>
                <v-list-item-title>API token</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="state.hasJiraAuthToken" @click="onDeleteJiraUserToken">
                <v-list-item-icon>
                  <v-icon>$fabJira</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Delete my Jira user token</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <v-list-item :href="state.helpLinkHelpCenter" target="_blank">
          <v-list-item-icon>
            <v-icon>
              $lifering-light
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Helpcenter
          </v-list-item-title>
        </v-list-item>

        <v-list-item @click="openIntercom">
          <v-list-item-icon>
            <v-icon>
              $chat-light
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Talk to support
          </v-list-item-title>
        </v-list-item>

        <v-list-item @click="onlogOut">
          <v-list-item-icon>
            <v-icon>
              $sign-out-light
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            Sign out
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<style lang="sass" scoped></style>
